<script lang="ts">
export default {
  name: "BaseMediaItem",
  inheritAttrs: false,
};
</script>
<script setup lang="ts">
import BaseBadge from "@/components/Base/Badge.vue";

export type MediaItem = {
  id: string;
  title: string;
  description: string;
  path?: string;
  experimental?: boolean;
  new?: boolean;
  dataTestId?: string;
};

type Props = {
  item: MediaItem;
  hideBadge?: boolean;
  withRouterLink?: boolean;
};

const { t } = useI18n();
const props = withDefaults(defineProps<Props>(), {
  hideBadge: false,
  withRouterLink: true,
});
</script>

<template>
  <div class="media-item" :data-id="item.id">
    <div class="media-item__media">
      <slot name="media"></slot>
    </div>
    <div class="media-item__content">
      <h3 class="media-item__title">
        <router-link
          v-if="withRouterLink && item.path"
          :to="item.path"
          :data-testid="item.dataTestId"
          class="media-item__link stretched-link"
          v-bind="$attrs"
        >
          <slot name="title">
            {{ item.title }}
          </slot>
        </router-link>
        <a
          v-else-if="item.path"
          :href="item.path as string"
          :data-testid="item.dataTestId"
          v-bind="$attrs"
          class="media-item__link stretched-link"
        >
          {{ item.title }}
        </a>
        <span v-if="item.new && !props.hideBadge" class="media-item__badge">
          <BaseBadge v-if="item.new" state="purple">
            {{ t("labels.new") }}
          </BaseBadge>
        </span>
      </h3>
      <div class="media-item__description">
        {{ item.description }}
      </div>
      <div v-if="item.experimental" class="media-item__badge">
        <span class="badge bg-primary fw-medium">
          {{ t("startPage.tools.experimental") }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.media-item {
  position: relative;
  display: flex;
  gap: $spacer-3;
  padding: $spacer-3;
  background-color: $color-surface-white;
  overflow: hidden;
  min-width: 0;
  height: 100px;
  border-radius: $border-radius-lg;
  border: 1px solid $border-color;
  box-shadow: $shadow-brand;
  transition:
    transform 0.1s ease-out,
    box-shadow 0.1s ease-out;

  &:hover {
    background-color: $hover-color;
    transform: scale(1.03);
  }

  &__media {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    flex: none;
    width: 64px;
    height: 64px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 1;
    overflow: hidden;
  }

  &__title {
    display: flex;
    align-items: center;
    align-self: flex-start;
    width: 100%;
    min-width: 0;
    margin-bottom: $spacer-1;
    font-size: $font-size-base;
    line-height: 1.3;
    font-weight: $font-weight-medium;
  }

  &__link {
    color: $color-text;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: $spacer-1;
  }

  &__description {
    font-size: $font-size-sm;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.35;
  }

  &__badge {
    position: absolute;
    top: 10px;
    right: -7px;
  }

  @include media-breakpoint-down(md) {
    gap: $spacer-75;
    padding: $spacer-75;
    height: 56px;

    &__media {
      width: 30px;
      height: 30px;
    }

    &__title {
      font-size: $font-size-base;
      margin-bottom: 0;
    }

    &__description {
      display: none;
    }
  }
}
</style>
